import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Plus } from "lucide-react";
import { api, formatEUR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  SHIPMENT_STATUS_LABELS,
  SHIPMENT_STATUS_BADGE_CLASSES,
} from "../lib/shipment-statuses";

const TYPE_LABELS = {
  ropa: "Ropa",
  alimentos: "Alimentos",
  electronica: "Electrónica",
  tv: "TV",
  medicinas: "Medicinas",
  herramientas: "Herramientas",
  otros: "Otros",
};

// Phase 47 — labels/colors sourced from the shared module so a change
// in one place propagates everywhere (admin + tracking + list).
const STATUS_LABELS = SHIPMENT_STATUS_LABELS;
const STATUS_CLASSES = SHIPMENT_STATUS_BADGE_CLASSES;

export default function MisEnviosPage() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    const load = () =>
      api
        .get("/shipments")
        .then((r) => setItems(r.data))
        .catch(() => setItems([]));
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  if (items === null) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-16">
        <div className="h-64 rounded-2xl bg-neutral-100 animate-pulse" />
      </div>
    );
  }

  return (
    <div
      data-testid="mis-envios-page"
      className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-10"
    >
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Mis envíos</h1>
        <Button
          asChild
          data-testid="mis-envios-new-btn"
          className="rounded-full bg-[#00953B] hover:bg-[#007a30] text-white"
        >
          <Link to="/enviar-paquete/nuevo">
            <Plus className="h-4 w-4 mr-1" /> Nuevo envío
          </Link>
        </Button>
      </div>

      {items.length === 0 ? (
        <div
          data-testid="mis-envios-empty"
          className="rounded-2xl border border-neutral-200 bg-white p-10 text-center"
        >
          <div className="mx-auto h-16 w-16 rounded-2xl bg-[#F1FAF3] grid place-items-center text-[#00953B] mb-4">
            <Package className="h-8 w-8" />
          </div>
          <h2 className="text-xl font-black tracking-tight">
            Aún no tienes envíos
          </h2>
          <p className="text-neutral-500 mt-2 max-w-md mx-auto">
            Envía tu primer paquete a los campamentos saharauis en unos pocos
            pasos.
          </p>
          <Button
            asChild
            className="mt-6 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white"
          >
            <Link to="/enviar-paquete">Empezar</Link>
          </Button>
        </div>
      ) : (
        <div className="rounded-2xl border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-neutral-500 bg-neutral-50">
              <tr>
                <th className="p-3">Fecha</th>
                <th>Envío</th>
                <th>Tipo</th>
                <th>Destinatario</th>
                <th>Coste</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr
                  key={s.id}
                  data-testid={`mis-envios-row-${s.id}`}
                  className="border-t"
                >
                  <td className="p-3 whitespace-nowrap">
                    {new Date(s.created_at).toLocaleDateString("es-ES")}
                  </td>
                  <td className="font-mono">#{s.id.slice(0, 8)}</td>
                  <td>{TYPE_LABELS[s.shipment_type] || s.shipment_type}</td>
                  <td>
                    <div className="font-semibold">{s.recipient_name}</div>
                    <div className="text-xs text-neutral-500">
                      {s.recipient_camp}
                    </div>
                  </td>
                  <td className="font-semibold">{formatEUR(s.fee_eur)}</td>
                  <td>
                    <Badge className={STATUS_CLASSES[s.status] || "bg-neutral-200"}>
                      {STATUS_LABELS[s.status] || s.status}
                    </Badge>
                  </td>
                  <td className="pr-3 whitespace-nowrap">
                    <Link
                      to={`/envio-paquete/${s.id}`}
                      className="text-[#00953B] font-semibold hover:underline"
                    >
                      Ver
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
